// Errores de las líneas de la venta (agregar, quitar, cotizar, cobrar y entregar) → qué hace el
// vendedor. Una sola tabla para que Producto, Cotización y Entrega digan lo mismo por el mismo code.
import { ApiError } from '@/api/errors';

/** Un mensaje por code del contrato de errores del ciclo de inventario multi-item. */
const MESSAGE_BY_CODE: Record<string, string> = {
    INSUFFICIENT_STOCK:
        'No hay piezas suficientes de este producto en la tienda. Elige otro producto o agrega menos piezas.',
    COMPONENT_OUT_OF_STOCK: 'A este kit le falta un componente en la tienda. Elige otro producto.',
    PRODUCT_NOT_SELLABLE: 'Este producto no se vende suelto. Quítalo y elige otro.',
    SALE_TOO_LARGE: 'La venta pasaría de 99 piezas. Cobra esta venta y registra el resto en otra.',
    CATALOG_UNAVAILABLE:
        'El inventario no respondió. No cobres todavía; vuelve a intentar en un momento.',
    PRODUCT_NOT_FINANCEABLE:
        'Ese producto no se puede financiar con SureKeep. Elige otro producto para financiar o cóbralo de contado.',
    IDENTIFIER_MISMATCH:
        'Esa pieza no corresponde a este producto o a esta tienda. Escanea la pieza correcta.',
    IDENTIFIER_NOT_AVAILABLE: 'Esa pieza ya se vendió o la tomó otra venta. Escanea otra.',
    RESERVATION_NOT_FOUND:
        'Lo apartado de esta venta ya no está en el inventario. La venta quedó marcada para revisión de inventario; avisa a tu gerente.',
    RESERVATION_EXPIRED: 'Lo apartado venció. Vuelve a agregar el producto antes de cobrar.',
    SESSION_CONCURRENT_UPDATE:
        'La venta cambió en otra pantalla. Recarga la venta y vuelve a intentar.',
    LINE_ALREADY_RESERVED:
        'Este producto ya está apartado para la venta. Recarga la venta y revisa los productos.',
    LINE_NOT_FOUND: 'Ese producto ya no está en la venta. Recarga la venta y revisa los productos.',
    PAID_SALE_LOCKED:
        'La venta ya tiene un cobro registrado: ya no puedes agregar ni quitar productos.',
    LINE_SKU_MISMATCH: 'Esa pieza no es de este producto de la venta. Revisa qué estás escaneando.',
    PRODUCT_NOT_FOUND:
        'Ese producto ya no está en el catálogo de la tienda. Actualiza el catálogo y elige otro.',
    SALE_PHONE_LIMIT_REACHED:
        'Esta venta ya tiene un teléfono. Para llevar otro, cámbialo con «Cambiar».',
    REFERRED_SALE_PHONE_NOT_ALLOWED: 'La venta se derivó: solo puedes agregar extras de contado.',
};

const SESSION_RELOAD_CODES = new Set([
    'SESSION_CONCURRENT_UPDATE',
    'LINE_NOT_FOUND',
    'LINE_ALREADY_RESERVED',
]);
const CATALOG_RELOAD_CODES = new Set([
    'INSUFFICIENT_STOCK',
    'COMPONENT_OUT_OF_STOCK',
    'PRODUCT_NOT_SELLABLE',
    'PRODUCT_NOT_FOUND',
]);

function dataOf(error: unknown, code: string): Record<string, unknown> | undefined {
    if (!(error instanceof ApiError) || error.code !== code) return undefined;
    return typeof error.data === 'object' && error.data !== null
        ? (error.data as Record<string, unknown>)
        : undefined;
}

/** SKU del componente que le falta al kit (`COMPONENT_OUT_OF_STOCK`). */
export function componentSkuOf(error: unknown): string | undefined {
    const sku = dataOf(error, 'COMPONENT_OUT_OF_STOCK')?.sku;
    return typeof sku === 'string' && sku !== '' ? sku : undefined;
}

/** Línea y SKU de la pieza que no corresponde (`IDENTIFIER_MISMATCH`). */
export function identifierMismatchOf(error: unknown): { lineId: string; sku: string } | undefined {
    const data = dataOf(error, 'IDENTIFIER_MISMATCH');
    if (typeof data?.lineId !== 'string' || typeof data.sku !== 'string') return undefined;
    return { lineId: data.lineId, sku: data.sku };
}

/** Mensaje accionable de un error de líneas; `undefined` si el code no es de este contrato. */
export function lineErrorMessage(error: unknown): string | undefined {
    if (!(error instanceof ApiError) || !Object.hasOwn(MESSAGE_BY_CODE, error.code))
        return undefined;
    const sku = componentSkuOf(error);
    if (sku !== undefined) {
        return `A este kit le falta ${sku} en la tienda. Elige otro producto.`;
    }
    return MESSAGE_BY_CODE[error.code];
}

/** La sesión en pantalla ya no es la real: hay que releerla antes del siguiente intento. */
export function needsSessionReload(error: unknown): boolean {
    return error instanceof ApiError && SESSION_RELOAD_CODES.has(error.code);
}

/** El disponible en pantalla ya no es el real: hay que releer el catálogo. */
export function needsCatalogReload(error: unknown): boolean {
    return error instanceof ApiError && CATALOG_RELOAD_CODES.has(error.code);
}
